import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ScheduleOccurrenceDto } from '@fas/types';
import { Alert, Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@fas/ui';
import { apiRequest, errorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { OccurrenceCard } from './Timetable';

/** Today's classes for the signed-in faculty member (dashboard, §57 faculty home). */
export function TodayCard() {
  const today = useQuery({
    queryKey: ['/schedules', 'today'],
    queryFn: ({ signal }) => apiRequest<ScheduleOccurrenceDto[], { date: string; timeZone: string }>('GET', '/schedules/today', { signal }),
    refetchInterval: 5 * 60_000,
  });
  const items = today.data?.data ?? [];
  const date = today.data?.meta.date;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Today&apos;s classes</CardTitle>
        <CardDescription>
          {date ? formatDate(date) : 'Today'} ·{' '}
          <Link to="/my-schedule" className="font-medium text-blue-700 hover:underline">
            View my week
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {today.isLoading ? <Skeleton className="h-20 w-full" /> : null}
        {today.error ? <Alert tone="danger">{errorMessage(today.error)}</Alert> : null}
        {today.data && items.length === 0 ? <p className="text-sm text-slate-600">You have no classes scheduled today.</p> : null}
        {items.length > 0 ? (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((o) => (
              <li key={`${o.scheduleId}-${o.date}`}>
                <OccurrenceCard o={o} />
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-3 text-xs text-slate-500">Clock-in for class sessions becomes available with attendance tracking.</p>
      </CardContent>
    </Card>
  );
}
